// Pure-logic insight derivation for the Project view's "Now" card.
// All functions take SprintListItem[] sorted newest-first (matches what the
// API returns) and return derived state. No Vue, no fetch, no DOM — fully
// testable, reusable in Phase 4's programme dashboard.

import type { MetricEnvelope, RagBand, SprintListItem } from "@/api/types";

export type HealthBand = "green" | "amber" | "red" | "unknown";

export interface ProjectHealth {
  band: HealthBand;
  reason: string;
}

export type RiskSeverity = "high" | "medium" | "low";

export interface Risk {
  metric: string; // e.g., "M16"
  label: string; // e.g., "Mid-sprint additions"
  severity: RiskSeverity;
  message: string;
  rule: "rising_trend" | "threshold_crossed" | "absolute_red" | "p0p1_spillover" | "size_violation";
}

export interface MetricSpark {
  metricId: string;
  label: string;
  current: number | null;
  previous: number | null;
  delta: number | null;
  rag: RagBand;
  series: Array<number | null>; // oldest → newest, length up to N
}

export const HEADLINE_METRIC_IDS = ["M7", "M14", "M16", "M17"] as const;

const METRIC_LABELS: Record<string, string> = {
  M7: "Completion %",
  M8: "Size-compliant %",
  M9: "Size violations",
  M10: "Carryover %",
  M10_pct: "Carryover %",
  M14: "P0/P1 share",
  M15: "P0/P1 spillover",
  M16: "Mid-sprint additions",
  M17: "Missing estimates",
  M18: "Start readiness",
  M19: "Avg cycle time",
};

export function metricLabel(id: string): string {
  return METRIC_LABELS[id] ?? id;
}

function readMetric(sprint: SprintListItem | undefined, id: string): MetricEnvelope | undefined {
  return sprint?.headline_metrics?.[id];
}

function readValue(env: MetricEnvelope | undefined): number | null {
  if (!env) return null;
  if (env.value != null && typeof env.value === "number") return env.value;
  if (env.pct != null && typeof env.pct === "number") return env.pct;
  return null;
}

function readRag(env: MetricEnvelope | undefined): RagBand {
  return (env?.rag as RagBand | undefined) ?? "none";
}

/**
 * Derive an overall project health band from the latest sprint's headline metrics.
 * Rule:
 *   - any headline RAG=red → red
 *   - else 2+ headline RAG=amber → amber
 *   - else if at least one headline has a known RAG → green
 *   - else unknown (no data yet)
 */
export function deriveProjectHealth(sprints: SprintListItem[]): ProjectHealth {
  const latest = sprints[0];
  if (!latest) return { band: "unknown", reason: "no sprints available" };

  const bands = HEADLINE_METRIC_IDS.map((id) => ({ id, band: readRag(readMetric(latest, id)) }));
  const reds = bands.filter((b) => b.band === "red");
  const ambers = bands.filter((b) => b.band === "amber");
  const knowns = bands.filter((b) => b.band !== "none");

  if (reds.length > 0) {
    return {
      band: "red",
      reason: `${reds.length} headline metric${reds.length > 1 ? "s" : ""} red (${reds.map((r) => r.id).join(", ")})`,
    };
  }
  if (ambers.length >= 2) {
    return {
      band: "amber",
      reason: `${ambers.length} headline metrics amber (${ambers.map((r) => r.id).join(", ")})`,
    };
  }
  if (knowns.length > 0) {
    return { band: "green", reason: "all headline metrics green" };
  }
  return { band: "unknown", reason: "headline metrics not yet computed" };
}

/**
 * Build a sparkline + delta object for a single metric across the last N sprints.
 * `sprints` is newest-first; the returned `series` is oldest-first so chart
 * libraries draw left-to-right naturally.
 */
export function metricSpark(sprints: SprintListItem[], metricId: string, n = 6): MetricSpark {
  const window = sprints.slice(0, n);
  const values = window.map((s) => readValue(readMetric(s, metricId)));
  const series = [...values].reverse();
  const current = values[0] ?? null;
  const previous = values[1] ?? null;
  const delta = current != null && previous != null ? current - previous : null;
  return {
    metricId,
    label: metricLabel(metricId),
    current,
    previous,
    delta,
    rag: readRag(readMetric(sprints[0], metricId)),
    series,
  };
}

/**
 * Scan the last few sprints for rising-risk patterns. Each rule emits at most
 * one Risk; the caller takes top 3 sorted by severity.
 *
 * Rules:
 *   1. Latest sprint has any headline metric in RED       → "absolute_red"
 *   2. M16 (mid-sprint additions) trending up 3+ sprints  → "rising_trend"
 *   3. M10 carryover increased > 5pp vs previous          → "rising_trend"
 *   4. M17 missing estimates increased > 0 vs previous    → "rising_trend"
 *   5. Latest M15 (P0/P1 spillover) > 0                   → "p0p1_spillover"
 *   6. Latest M9 (size violations) > 0                    → "size_violation"
 */
export function scanRisks(sprints: SprintListItem[]): Risk[] {
  const out: Risk[] = [];
  const latest = sprints[0];
  if (!latest) return out;
  const prev = sprints[1];

  // Rule 1: hard red on a headline
  for (const id of HEADLINE_METRIC_IDS) {
    if (readRag(readMetric(latest, id)) === "red") {
      out.push({
        metric: id,
        label: metricLabel(id),
        severity: "high",
        message: `${metricLabel(id)} is red this sprint`,
        rule: "absolute_red",
      });
    }
  }

  // Rule 2: M16 rising over 3 sprints
  const m16 = sprints.slice(0, 3).map((s) => readValue(readMetric(s, "M16")));
  if (m16.length === 3 && m16.every((v) => v != null) && m16[0]! > m16[1]! && m16[1]! > m16[2]!) {
    out.push({
      metric: "M16",
      label: "Mid-sprint additions",
      severity: "medium",
      message: "Mid-sprint additions trending up 3 sprints in a row",
      rule: "rising_trend",
    });
  }

  // Rule 3: M10 carryover jumped > 5 percentage points
  const c10cur = readValue(readMetric(latest, "M10")) ?? readValue(readMetric(latest, "M10_pct"));
  const c10prev = readValue(readMetric(prev, "M10")) ?? readValue(readMetric(prev, "M10_pct"));
  if (c10cur != null && c10prev != null && c10cur - c10prev > 0.05) {
    out.push({
      metric: "M10",
      label: "Carryover",
      severity: "medium",
      message: `Carryover up ${Math.round((c10cur - c10prev) * 100)}pp vs previous sprint`,
      rule: "rising_trend",
    });
  }

  // Rule 4: M17 missing estimates increased
  const m17cur = readValue(readMetric(latest, "M17"));
  const m17prev = readValue(readMetric(prev, "M17"));
  if (m17cur != null && m17prev != null && m17cur > m17prev && m17cur > 0) {
    out.push({
      metric: "M17",
      label: "Missing estimates",
      severity: "low",
      message: `Missing-estimate share crept up to ${Math.round(m17cur * 100)}%`,
      rule: "rising_trend",
    });
  }

  // Rule 5: P0/P1 spillover
  const m15cur = readValue(readMetric(latest, "M15"));
  if (m15cur != null && m15cur > 0) {
    out.push({
      metric: "M15",
      label: "P0/P1 spillover",
      severity: "high",
      message: `${Math.round(m15cur)} P0/P1 issue${m15cur > 1 ? "s" : ""} carried over`,
      rule: "p0p1_spillover",
    });
  }

  // Rule 6: Size violations
  const m9cur = readValue(readMetric(latest, "M9"));
  if (m9cur != null && m9cur > 0) {
    out.push({
      metric: "M9",
      label: "Size violations",
      severity: "medium",
      message: `${Math.round(m9cur)} issue${m9cur > 1 ? "s" : ""} sized > 8 SP entered the sprint`,
      rule: "size_violation",
    });
  }

  // Sort by severity (high first), then dedupe by metric id keeping the highest-severity entry.
  const sevOrder: Record<RiskSeverity, number> = { high: 0, medium: 1, low: 2 };
  out.sort((a, b) => sevOrder[a.severity] - sevOrder[b.severity]);
  const seen = new Set<string>();
  return out.filter((r) => {
    if (seen.has(r.metric)) return false;
    seen.add(r.metric);
    return true;
  });
}

/**
 * Build a one-sentence narrative summarising the latest sprint. Always returns
 * the same template shape so leadership can scan a row of projects.
 */
export function buildNarrative(sprints: SprintListItem[], risks: Risk[]): string {
  const latest = sprints[0];
  if (!latest) return "No sprint data yet.";

  const m7 = readMetric(latest, "M7");
  const m7val = readValue(m7);
  const m7rag = readRag(m7);
  const isActive = latest.sprint_state === "active";

  // Active sprints are still in flight; "closed at X%" misreads partial data
  // as a final score. RAG bands are calibrated against close-of-sprint values
  // — drop the colour qualifier for active and prefix the percentage as
  // "currently at" so leadership can scan in-progress vs. final.
  let completionPart: string;
  if (m7val == null) {
    completionPart = latest.sprint_name;
  } else if (isActive) {
    completionPart = `${latest.sprint_name} is currently at ${Math.round(m7val * 100)}% completion (in progress)`;
  } else {
    const colour = m7rag === "green" ? "green" : m7rag === "amber" ? "amber" : "red";
    completionPart = `${latest.sprint_name} closed at ${Math.round(m7val * 100)}% completion (${colour})`;
  }

  if (risks.length === 0) {
    if (!isActive && m7rag === "green") {
      return `${completionPart} with all headline metrics green and no rising risks.`;
    }
    return `${completionPart}. No rising risks flagged.`;
  }

  // Risks are already surfaced as a list of cards above the narrative — don't
  // echo the top one verbatim. Just summarise the count so the narrative
  // stays a single, scannable sentence.
  const noun = risks.length === 1 ? "risk" : "risks";
  return `${completionPart}; ${risks.length} rising ${noun} to watch.`;
}

/**
 * Format the project header cadence summary.
 */
export interface CadenceSummary {
  count: number;
  lastClosed?: string;
  lastClosedAt?: string;
  cadenceDays?: number;
}

export function cadenceSummary(sprints: SprintListItem[]): CadenceSummary {
  if (sprints.length === 0) return { count: 0 };
  const closed = sprints.filter((s) => s.sprint_state === "closed");
  const lastClosed = closed[0];
  return {
    count: sprints.length,
    lastClosed: lastClosed?.sprint_name,
    lastClosedAt: lastClosed?.sprint_end,
    cadenceDays: medianGapDays(closed),
  };
}

/**
 * Median number of days between consecutive closed-sprint start dates.
 *
 * Why median, not pairwise: real teams have one-off hiatuses (holidays,
 * incident response). A single 50-day gap among 14-day sprints used to drag
 * the headline "cadence" reading to 50; the median is robust to that.
 */
function medianGapDays(closedNewestFirst: SprintListItem[]): number | undefined {
  if (closedNewestFirst.length < 2) return undefined;
  const day = 1000 * 60 * 60 * 24;
  const gaps: number[] = [];
  for (let i = 0; i < closedNewestFirst.length - 1; i++) {
    const a = Date.parse(closedNewestFirst[i].sprint_start);
    const b = Date.parse(closedNewestFirst[i + 1].sprint_start);
    if (Number.isNaN(a) || Number.isNaN(b)) continue;
    gaps.push(Math.abs(a - b) / day);
  }
  if (gaps.length === 0) return undefined;
  gaps.sort((x, y) => x - y);
  const mid = Math.floor(gaps.length / 2);
  const median = gaps.length % 2 === 1 ? gaps[mid] : (gaps[mid - 1] + gaps[mid]) / 2;
  return Math.round(median);
}

/**
 * Time-progress for an in-flight sprint. Returns null when start/end can't be
 * parsed or when the sprint is not active. The Now card uses this so leadership
 * sees mid-sprint scores in context (e.g., "day 5 of 14, 35% time elapsed").
 *
 * RAG bands and deltas are calibrated against close-of-sprint values; the
 * caller pairs this with a disclaimer so partial data isn't read as a verdict.
 */
export interface SprintProgress {
  totalDays: number;
  elapsedDays: number;
  pctElapsed: number; // [0, 1], may exceed 1 if past end
  isOverdue: boolean;
}

export function sprintProgress(
  sprint: SprintListItem | undefined | null,
  now: Date = new Date(),
): SprintProgress | null {
  if (!sprint || sprint.sprint_state !== "active") return null;
  const start = Date.parse(sprint.sprint_start);
  const end = Date.parse(sprint.sprint_end);
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null;
  const day = 1000 * 60 * 60 * 24;
  const totalDays = Math.max(1, Math.round((end - start) / day));
  const elapsedRaw = (now.getTime() - start) / day;
  const elapsedDays = Math.max(0, Math.round(elapsedRaw));
  const pctElapsed = Math.max(0, (now.getTime() - start) / (end - start));
  return {
    totalDays,
    elapsedDays,
    pctElapsed,
    isOverdue: now.getTime() > end,
  };
}
